import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

const EMPTY_FORM = { title: "", description: "", category: "", due_date: "", state_tags: [], lock_sequential: true };

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.courses.list().then(setCourses).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title,
      description: c.description || "",
      category: c.category || "",
      due_date: c.due_date ? c.due_date.slice(0, 10) : "",
      state_tags: c.state_tags ? c.state_tags.split(",").filter(Boolean) : [],
      lock_sequential: !!c.lock_sequential,
    });
    setModalOpen(true);
  };

  const toggleState = (tag) => {
    setForm((f) => ({
      ...f,
      state_tags: f.state_tags.includes(tag) ? f.state_tags.filter((t) => t !== tag) : [...f.state_tags, tag],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        due_date: form.due_date || null,
        state_tags: form.state_tags.join(","),
        lock_sequential: form.lock_sequential ? 1 : 0,
      };
      if (editingId) await api.courses.update(editingId, payload);
      else await api.courses.create(payload);
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const togglePublish = async (course) => {
    await api.courses.update(course.id, { is_published: course.is_published ? 0 : 1 });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this course and all its lessons?")) return;
    await api.courses.remove(id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Courses</h1>
          <p className="text-sm text-gray-500">{courses.length} courses</p>
        </div>
        <button onClick={openNew} className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: "#154E93" }}>
          + New Course
        </button>
      </div>

      {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: "#154E93" }}>
            <tr className="text-white text-left">
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Compliance</th>
              <th className="px-4 py-2">Due Date</th>
              <th className="px-4 py-2">Lessons</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">
                  <Link to={`/admin/courses/${c.id}`} className="hover:underline" style={{ color: "#154E93" }}>{c.title}</Link>
                </td>
                <td className="px-4 py-2">{c.category}</td>
                <td className="px-4 py-2">
                  {(c.state_tags || "").split(",").filter(Boolean).map((tag) => (
                    <span key={tag} className="inline-block bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded mr-1">{tag}</span>
                  ))}
                </td>
                <td className="px-4 py-2">{c.due_date ? c.due_date.slice(0, 10) : "—"}</td>
                <td className="px-4 py-2">{c.lesson_count}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.is_published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {c.is_published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-2 space-x-3 whitespace-nowrap">
                  <button onClick={() => openEdit(c)} className="text-blue-600 hover:underline text-xs">Settings</button>
                  <button onClick={() => togglePublish(c)} className="text-blue-600 hover:underline text-xs">
                    {c.is_published ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => remove(c.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                </td>
              </tr>
            ))}
            {courses.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No courses yet — create your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModalOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4" style={{ color: "#154E93" }}>{editingId ? "Course Settings" : "New Course"}</h2>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm mb-3" />
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Onboarding, Dementia Care, Safety" className="w-full border rounded px-2 py-1.5 text-sm mb-3" />
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm mb-3" rows={3} />

            <label className="block text-xs font-medium text-gray-600 mb-1">Meets compliance requirement for</label>
            <div className="flex gap-3 mb-3">
              {["NH", "MA"].map((tag) => (
                <label key={tag} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={form.state_tags.includes(tag)} onChange={() => toggleState(tag)} />
                  {tag === "NH" ? "New Hampshire" : "Massachusetts"}
                </label>
              ))}
            </div>

            <label className="block text-xs font-medium text-gray-600 mb-1">Deadline (only admins can set/change this)</label>
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm mb-3" />

            <label className="flex items-center gap-2 text-sm mb-4">
              <input type="checkbox" checked={form.lock_sequential} onChange={(e) => setForm({ ...form, lock_sequential: e.target.checked })} />
              Lock lessons in order (caregivers must pass each lesson before the next unlocks)
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded border">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm rounded text-white" style={{ backgroundColor: "#154E93" }}>{editingId ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
